/**
 * Page languages: languages that exist on a few pages only, next to the 14
 * site languages every page is routable in (SUPPORTED_LANGUAGES in
 * server/_core/seo.ts, supportedLanguages in client/src/locales/index.ts).
 * Thai and Traditional Chinese so far, each on /ai-music-video-generator.
 *
 * Why (Oct 13 2026): Thailand and Taiwan search for an AI music video maker
 * in their own words (about 690 and 790 Google searches a month, Ahrefs) and
 * the site had nothing in either language. Making them site languages would
 * have put about 450 English-text pages under /th/ and /tw/ in the sitemap
 * and added two hreflang links to every other page. A page language changes
 * nothing outside its own pages:
 *  - /{code}{path} is a page of its own only for the paths listed here:
 *    self-canonical, in the sitemap and in that page's hreflang cluster (the
 *    cluster of all 14 other versions gains it too, hreflang is reciprocal).
 *  - /{code}/anything-else redirects (302, the language may get that page
 *    later) to the same path without the prefix, on the server
 *    (getPageLanguageRedirect in server/_core/seo.ts) and in the app
 *    (LanguageRouter in client/src/App.tsx). Links on a page-language page
 *    to other pages are built without the prefix
 *    (client/src/lib/languagePath.ts).
 *  - The app never picks a page language from the browser's language or
 *    from localStorage, only from the URL (client/src/i18n.ts).
 *  - The language menu and the "this page is available in…" banner offer it
 *    only on its own pages.
 *
 * `code` is the URL prefix, the i18n language and the file name
 * (client/src/locales/{code}.ts, shared/mvGeneratorLocales/{code}.ts). It
 * has to be two lowercase letters with no hyphen and must not start with
 * another language's code: i18next runs with `load: 'languageOnly'`, which
 * cuts "zh-tw" down to Simplified "zh", and several lookups read the first
 * two letters of a language code. Hence "tw" for Traditional Chinese (Taiwan
 * usage; `tag` says zh-Hant, so Hong Kong and Macau readers get it too).
 * A prefix is permanent once the page is indexed.
 *
 * To switch a language off, empty its `paths`: every output returns to what
 * it was before the language existed. To give it another page, add the path
 * and that page's copy.
 *
 * No imports on purpose: the client's entry bundle reads this file.
 */
export type PageLanguage = {
  /** URL prefix, i18n language and locale file name. */
  code: string;
  /** BCP 47 tag: <html lang>, hreflang and schema.org inLanguage. */
  tag: string;
  /** Its own name, for the language menu on its pages. */
  nativeName: string;
  /** English base paths that are a page of their own in this language. */
  paths: readonly string[];
  /**
   * Browsers the banner offers it to: tested against the browser's first
   * language (navigator.languages[0]).
   */
  browserLanguages: RegExp;
};

export const PAGE_LANGUAGES: readonly PageLanguage[] = [
  {
    code: "th",
    tag: "th",
    nativeName: "ไทย",
    paths: ["/ai-music-video-generator"],
    browserLanguages: /^th\b/i,
  },
  {
    code: "tw",
    tag: "zh-Hant",
    nativeName: "繁體中文",
    paths: ["/ai-music-video-generator"],
    browserLanguages: /^zh-(tw|hk|mo|hant)\b/i,
  },
];

export const PAGE_LANGUAGE_CODES: readonly string[] = PAGE_LANGUAGES.map(
  (language) => language.code,
);

export function getPageLanguage(code: string | undefined): PageLanguage | null {
  return PAGE_LANGUAGES.find((language) => language.code === code) ?? null;
}

/** Codes of the page languages that have this base path, in list order. */
export function pageLanguagesForPath(basePath: string): string[] {
  return PAGE_LANGUAGES.filter((language) =>
    language.paths.includes(basePath),
  ).map((language) => language.code);
}

/** True when /{code}{basePath} is a page of its own. */
export function isPageLanguagePath(code: string, basePath: string): boolean {
  return getPageLanguage(code)?.paths.includes(basePath) ?? false;
}

/** BCP 47 tag of a language code. A site language is its own tag. */
export function languageTag(code: string): string {
  return getPageLanguage(code)?.tag ?? code;
}

/**
 * Splits a path whose first segment is a page language's code:
 * "/th/pricing?x=1" gives { code: "th", basePath: "/pricing", ownPage: false }.
 * Null for any other path. `rest` is the path after the prefix (trailing
 * slash, query and hash kept): what a stray URL redirects to. It always
 * starts with exactly one slash, so the redirect stays on this site:
 * "/th//evil.example" and "/th/\evil.example" give "/evil.example", never
 * the protocol-relative "//evil.example" a browser would follow to another
 * host.
 */
export function splitPageLanguagePath(pathname: string): {
  code: string;
  basePath: string;
  ownPage: boolean;
  rest: string;
} | null {
  // [\s\S], not ".": a path with a line break in it is split like any other.
  const match = /^\/([^/?#]+)([\s\S]*)$/.exec(pathname);
  const language = getPageLanguage(match?.[1]?.toLowerCase());
  if (!match || !language) return null;
  const after = match[2] ?? "";
  const segments = (after.split(/[?#]/)[0] ?? "").split("/").filter(Boolean);
  const basePath = `/${segments.join("/")}`;
  return {
    code: language.code,
    basePath,
    ownPage: language.paths.includes(basePath),
    // Slashes, backslashes (a browser reads "\" as "/") and whitespace or
    // control characters (a browser drops tabs and newlines) at the start
    // are all cut, then one slash is put back.
    rest: `/${after.replace(/^[/\\\s\u0000-\u001f]+/, "")}`,
  };
}

/**
 * The page language to offer a browser on this page (the "this page is
 * available in…" banner): the first one that has the page and whose
 * `browserLanguages` matches the browser's first language. Null on every
 * page no page language has, whatever the browser. `forced` is a code from
 * ?lang-suggest= (QA): it is offered whatever the browser's language, still
 * only on that language's own pages.
 */
export function pageLanguageForBrowser(
  browserLanguage: string | undefined,
  basePath: string,
  forced?: string | null,
): string | null {
  const forcedLanguage = forced ? getPageLanguage(forced) : null;
  if (forcedLanguage) {
    return forcedLanguage.paths.includes(basePath) ? forcedLanguage.code : null;
  }
  if (!browserLanguage) return null;
  return (
    PAGE_LANGUAGES.find(
      (language) =>
        language.paths.includes(basePath) &&
        language.browserLanguages.test(browserLanguage),
    )?.code ?? null
  );
}
